import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import App from './App';
import TauriLoader2 from './components/TauriLoader2/TauriLoader2';
import { jwtDecode } from 'jwt-decode';
import reportWebVitals from './reportWebVitals';
import appURL from './appURL.json';
import setting from './config.json';
import OpalAlertModal from './components/OpalAlertModal/OpalAlertModal';
import { addRecord, checkIfDatabaseExists, clearAllLocalData, clearDataByStoreName, deleteLocalDatabase, getRecordById, openDatabase, syncDataToLocal, updateRecord } from './localDB';
import { File } from './visualizations/visualizations';
import { transformSyncFile } from './components/OpalSyncFactoryData/OpalSyncFactoryData';

const root = createRoot(document.getElementById('root'));

const AuthenticationLoader = ({ onComplete }) => {


  const fetchUserPermission = async (token, extID) => {
    try {
      const response = await fetch(`${appURL.API_IDENTITY_GetUserPermission}?extID=${extID}&applicationcode=${setting?.AppCode}&companycode=${setting?.CompanyCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch data:', response.status);
        return;
      }

      const data = await response.json();
      const permission = JSON.stringify(data?.Permissions);
      const based64Permission = encodeBase64(permission);
      localStorage.setItem('permission', based64Permission);
      return data;
    } catch (error) {
      console.log(error);
    }
  };

  const checkDB = async (extID) => {
    try {
      const currentUserExtID = extID;
      const dbExists = await checkIfDatabaseExists();

      if (dbExists) {
        try {
          const logs = await getRecordById("Logs", 1);
          const lastUserExtID = logs?.LastAccount;

          if (!logs) {
            await clearAllLocalData();
            await addRecord("Logs", { ID: 1, LastAccount: currentUserExtID });
          } else if (logs && lastUserExtID !== currentUserExtID) {
            await clearAllLocalData();
            await updateRecord("Logs", { ...logs, LastAccount: currentUserExtID });
          }
        } catch (err) {
          console.error("Error reading/updating Logs store:", err);
        }
      }
    } catch (err) {
      console.error("checkDB failed:", err);
    }
  };

  const encodeBase64 = (str) => {
    return window.btoa(unescape(encodeURIComponent(str)));
  };


  useEffect(() => {
    const authenticate = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const decodeParam = (param) => param ? decodeURIComponent(param) : null;

        const tkn = decodeParam(params.get("tkn"));
        const devId = decodeParam(params.get("devId")) || localStorage.getItem('devId');
        const ext_id = decodeParam(params.get("ext_id")) || localStorage.getItem('ext_id');
        const name = decodeParam(params.get("name")) || localStorage.getItem('name');
        const title = decodeParam(params.get("title")) || localStorage.getItem('title');
        const usr_g = decodeParam(params.get("usr_g")) || localStorage.getItem('usr_g');
        const srv_date = decodeParam(params.get("srv_date")) || localStorage.getItem('srv_date');

        const c_id = decodeParam(params.get("c_id")) || localStorage.getItem('c_id');
        const c_cd = decodeParam(params.get("c_cd")) || localStorage.getItem('c_cd');
        const c_desc = decodeParam(params.get("c_desc")) || localStorage.getItem('c_desc');
        const c_str = decodeParam(params.get("c_str")) || localStorage.getItem('c_str');
        const c_end = decodeParam(params.get("c_end")) || localStorage.getItem('c_end');

        const p_days = decodeParam(params.get("p_days")) || localStorage.getItem('p_days');

        const token = tkn || localStorage.getItem('token');

        if (!token) {
          onComplete(false);
          return;
        }

        const payload = jwtDecode(token);
        const now = Math.floor(Date.now() / 1000);

        if (payload.exp < now) {
          onComplete(false);
          return;
        }

        await fetchUserPermission(token, ext_id);
        await checkDB(ext_id);

        localStorage.setItem('token', token);
        localStorage.setItem('devId', devId);
        localStorage.setItem('ext_id', ext_id);
        localStorage.setItem('name', name);
        localStorage.setItem('title', title);
        localStorage.setItem('usr_g', usr_g);
        localStorage.setItem('srv_date', srv_date);

        localStorage.setItem('c_id', c_id);
        localStorage.setItem('c_cd', c_cd);
        localStorage.setItem('c_desc', c_desc);
        localStorage.setItem('c_str', c_str);
        localStorage.setItem('c_end', c_end);

        localStorage.setItem('p_days', p_days);

        // Clean URL so token param disappears
        window.history.replaceState({}, document.title, window.location.pathname);

        onComplete(true);
      } catch (error) {
        console.error('Authentication failed', error);
        onComplete(false);
      }
    };

    authenticate();
  }, [onComplete]);

  return (
    <div style={{
      height: '100vh',
      width: '100vw',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
    </div>
  );
};

const Root = () => {
  const [authenticated, setAuthenticated] = useState(null);
  const [showUpdateNotice, setShowUpdateNotice] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);
  const LOCAL_VERSION_KEY = 'app_version';

  const checkAppVersion = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(appURL.API_Version, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        console.log("Failed to get version!");
        return;
      }

      const data = await response.json();
      const latest = data.version;

      const current = localStorage.getItem(LOCAL_VERSION_KEY);

      if (!current || current !== latest) {
        setLatestVersion(latest);
        setShowUpdateNotice(true);
      }
    } catch (err) {
      console.error('Error checking app version:', err);
    }
  };

  const syncFiles = async () => {
    try {
      const isInitialDataLoaded = JSON.parse(localStorage.getItem("isInitialDataLoaded") || false);
      if (isInitialDataLoaded) return;

      // const token = localStorage.getItem('token');

      // const response = await fetch(appURL.API_Version, {
      //   method: "GET",
      //   headers: {
      //     "Content-Type": "application/json",
      //     "Authorization": `Bearer ${token}`
      //   }
      // });

      const response = File;
      const transformFile = await transformSyncFile(response);
      await clearDataByStoreName('file');
      await syncDataToLocal('file', transformFile);

      // if (!response.ok) {
      //   console.log("Failed to get version!");
      //   return;
      // }

    } catch (err) {
      console.error('Error getting file:', err);
    }
  };


  const handleOk = () => {
    if (latestVersion) {
      localStorage.setItem(LOCAL_VERSION_KEY, latestVersion);
    }

    const lid = localStorage.getItem('lid');
    const appVersion = localStorage.getItem('app_version');

    window.localStorage.clear();

    if (lid) localStorage.setItem('lid', lid);
    if (appVersion) localStorage.setItem('app_version', appVersion);
    window.location.replace(window.location.pathname + '?t=' + Date.now());
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthParam = urlParams.has('tkn');

    const createLocalDb = async () => {
      await openDatabase();
    }

    createLocalDb();
    if (token) {
      try {
        const payload = jwtDecode(token);
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp > now) {
          setAuthenticated(true);
          return;
        }
      } catch (e) {
        // invalid token
      }
    }

    if (hasAuthParam) {
      // Run AuthenticationLoader to check for token in URL
      setAuthenticated(null);
    } else {
      // No valid token or auth param — redirect immediately
      setAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    if (authenticated === false) {
      // Redirect to login page if not authenticated
      const handleRedirect = async () => {
        const currentPath = encodeURIComponent(appURL.APP_BaseURL);
        window.location.href = `${appURL.API_IDENTITY_Authorize}/Identity/Account/Login?returnUrl=${currentPath}`;
      };

      handleRedirect();
    }

    if (authenticated === true) {
      checkAppVersion(); // <- run version check only when authenticated
      syncFiles();
    }
  }, [authenticated]);

  if (authenticated === null) {
    return <AuthenticationLoader onComplete={setAuthenticated} />;
  }

  return (
    <>
      {authenticated === true && (
        <HashRouter>
          <App />
        </HashRouter>
      )}

      <OpalAlertModal
        show={showUpdateNotice}
        onHide={() => {
          handleOk();
          setShowUpdateNotice(false);
        }}
        title="System"
        bodyText="There is a new update!"
        modalType="question"
        cancelButtonText={"Ok"}
        backdrop={"static"}
        keyboard={false}
      />
    </>
  );
};

root.render(<Root />);
reportWebVitals();